import { ProductHandle } from './enums/ProductHandle.enum';
import { Shopify } from './models/Shopify.model';
import { ViewProduct } from './models/ViewProduct.model';
import { HoneyloveAbTests } from './models/HoneyloveAbTests';
import { HoneyloveEnvironmentContext } from './models/HoneyloveWindow';
import dayjs from 'dayjs';
import { ProductCategory } from './enums/ProductCategory.enum';
import isBetween from 'dayjs/plugin/isBetween';
import { ShopifyCart } from './models/ShopifyCart.model';
dayjs.extend(isBetween);
export interface DiscountAmount {
  name: string;
  type: DiscountTypes;
  value: number;
  featured: boolean;
}

export interface ProductBundle {
  name: string;
  quantity: number;
  price: number;
}

export enum DiscountTypes {
  Percentage = 'percentage',
  Cents = 'cents',
}

export enum Discounts {
  bundle15Percent = 'BUNDLE-15-PERCENT',
  bundle20Percent = 'BUNDLE-20-PERCENT',
  threeItemBundle = 'THREE-ITEM-BUNDLE',
}

export const freeShippingThresholdInDollars = 75;

const bundleDiscountAmount = (itemCount: number, timedSaleActive: boolean, abTests?: HoneyloveAbTests): DiscountAmount => {
  // AB2021-13 test 15% vs 20% off bundle
  if (abTests?.['AB2021-13']) {
    return {
      name: Discounts.bundle15Percent,
      type: DiscountTypes.Percentage,
      value: itemCount > 1 ? 15 : 0,
      featured: false,
    };
  }

  const standardDiscount = timedSaleActive ? 10 : 20;

  return {
    name: Discounts.bundle20Percent,
    type: DiscountTypes.Percentage,
    value: itemCount > 1 ? standardDiscount : 0,
    featured: false,
  };
};

export const discountInLocalCurrency = (
  amountInCents: number,
  toViewPriceFn: (price: number, decimals?: number) => string,
  shopify?: Shopify,
  fractionDigits = 0,
): string => toViewPriceFn(Math.floor(amountInCents * (shopify?.currency.rate ?? 1)), fractionDigits);

export const getActiveDiscount = (itemCount: number, timedSaleActive: boolean, abTests?: HoneyloveAbTests): DiscountAmount =>
  bundleDiscountAmount(itemCount, timedSaleActive, abTests);

export const discountForDisplay = (
  itemCount: number,
  toViewPriceFn: (price: number, decimals?: number) => string,
  timedSaleActive: boolean,
  shopify?: Shopify,
  abTests?: HoneyloveAbTests,
): string => {
  const activeDiscount = getActiveDiscount(itemCount, timedSaleActive, abTests);
  return activeDiscount.type === DiscountTypes.Cents ? discountInLocalCurrency(activeDiscount.value, toViewPriceFn, shopify) : `${activeDiscount.value}%`;
};

export const productBundle = (product: ViewProduct): ProductBundle | undefined => {
  const has49Bundle = [ProductHandle.EverydayCottonBrief].includes(product.handle);
  const has59Bundle = [ProductHandle.SilhouetteBrief].includes(product.handle);
  return has49Bundle
    ? {
        name: Discounts.threeItemBundle,
        quantity: 3,
        price: 49,
      }
    : has59Bundle
    ? {
        name: Discounts.threeItemBundle,
        quantity: 3,
        price: 59,
      }
    : undefined;
};

export const applyWarehouseDiscount = (
  price: number,
  productType: ProductCategory,
  { warehouseDiscountPercentage, warehouseSaleEnabled }: HoneyloveEnvironmentContext,
): number => {
  const isGiftCard = productType === ProductCategory.GiftCard;
  const priceMultiplier = (100 - warehouseDiscountPercentage) / 100;
  const isActive = warehouseSaleEnabled && !isGiftCard;
  return isActive ? price * priceMultiplier : price;
};

export const applyTimedSaleDiscount = (
  price: number,
  productHandle: ProductHandle,
  { timedSaleActive, timedSaleDiscounts }: HoneyloveEnvironmentContext,
): number => {
  const discountPercentage = (timedSaleActive && timedSaleDiscounts[productHandle]) || 0;
  const priceMultiplier = (100 - discountPercentage) / 100;
  return price * priceMultiplier;
};

export const getTimeRemaining = (
  ISOStop: string,
): {
  hours: number;
  minutes: number;
  seconds: number;
} => {
  const dateFuture = dayjs(ISOStop);
  const dateNow = dayjs();
  const seconds = dateFuture.diff(dateNow, 'second');

  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  return {
    hours,
    minutes: minutes - hours * 60,
    seconds: Math.floor(seconds - hours * 3600 - (minutes - hours * 60) * 60),
  };
};

export const isInDateRange = (startISO: string, stopISO: string): boolean => {
  return dayjs().isBetween(startISO, stopISO);
};

export const getTotalForShippingDiscountFromCart = (cart: ShopifyCart) => {
  const totalSpentOnGiftCards =
    cart?.lineItems
      ?.filter((li) => li.product_type === ProductCategory.GiftCard)
      .map((li) => li.final_line_price)
      .reduce((a, b) => a + b, 0) ?? 0;

  return cart?.totalPrice ? cart?.totalPrice - totalSpentOnGiftCards : 0;
};
